import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, History, TrendingDown, TrendingUp, Minus, Clock, 
  FileText, ChevronRight, GitCompare, Download, Loader2,
  Plus, ArrowDown, ArrowUp, Equal, Sparkles
} from 'lucide-react';

const ContractVersionsPage = () => {
  const { contractId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState(null);
  const [versions, setVersions] = useState([]);
  const [riskEvolution, setRiskEvolution] = useState(null);
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [diffData, setDiffData] = useState(null);
  const [loadingDiff, setLoadingDiff] = useState(false);

  useEffect(() => {
    fetchVersions();
  }, [contractId]);

  const fetchVersions = async () => {
    try {
      const response = await api.get(`/versions/contract/${contractId}`);
      setContract({ id: contractId, title: response.data.contract_title });
      setVersions(response.data.versions || []);
      setRiskEvolution(response.data.risk_evolution);
    } catch (error) {
      console.error('Error fetching versions:', error);
      toast.error('Error al cargar versiones');
    } finally {
      setLoading(false);
    }
  };

  const handleVersionSelect = (versionId) => {
    setSelectedVersions(prev => {
      if (prev.includes(versionId)) {
        return prev.filter(id => id !== versionId);
      } else if (prev.length < 2) {
        return [...prev, versionId];
      } else {
        return [prev[1], versionId];
      }
    });
  };

  const fetchDiff = async () => {
    if (selectedVersions.length !== 2) {
      toast.error('Seleccione exactamente 2 versiones para comparar');
      return;
    }

    setLoadingDiff(true);
    try {
      const response = await api.post('/versions/compare', {
        version_id_old: selectedVersions[0],
        version_id_new: selectedVersions[1]
      });
      setDiffData(response.data);
    } catch (error) {
      console.error('Error fetching diff:', error);
      toast.error('Error al comparar versiones');
    } finally {
      setLoadingDiff(false);
    }
  };

  const getTrendIcon = () => {
    if (!riskEvolution) return <Minus className="w-5 h-5 text-slate-400" />;
    switch (riskEvolution.trend) {
      case 'improving':
        return <TrendingDown className="w-5 h-5 text-green-400" />;
      case 'worsening':
        return <TrendingUp className="w-5 h-5 text-red-400" />;
      default:
        return <Minus className="w-5 h-5 text-slate-400" />;
    }
  };

  const getTrendColor = () => {
    if (!riskEvolution) return 'text-slate-400';
    switch (riskEvolution.trend) {
      case 'improving':
        return 'text-green-400';
      case 'worsening':
        return 'text-red-400';
      default:
        return 'text-slate-400';
    }
  };

  const getRiskScoreColor = (score) => {
    if (score >= 70) return 'text-red-400 bg-red-500/20';
    if (score >= 40) return 'text-orange-400 bg-orange-500/20';
    if (score >= 20) return 'text-yellow-400 bg-yellow-500/20';
    return 'text-green-400 bg-green-500/20';
  };

  if (loading) {
    return (
      <div className="min-h-screen mesh-gradient flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen mesh-gradient">
      {/* Header */}
      <header className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/contracts" className="flex items-center space-x-3 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Contratos</span>
            </Link>
            <div className="flex items-center space-x-3">
              <History className="w-6 h-6 text-purple-500" />
              <span className="font-semibold font-['Outfit']">Historial de Versiones</span>
            </div>
            <div />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Contract Info */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold font-['Outfit'] mb-2">
            {contract?.title || 'Contrato'}
          </h1>
          <p className="text-slate-400">
            {versions.length} versiones guardadas
          </p>
        </div>

        {/* Risk Evolution Card */}
        {riskEvolution && versions.length >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 rounded-2xl bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-slate-700"
          >
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-6 h-6 text-purple-400" />
              <h2 className="text-lg font-semibold">Evolución del Riesgo</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {/* Trend */}
              <div className="text-center">
                <div className={`text-sm uppercase tracking-wider ${getTrendColor()} mb-2`}>
                  Tendencia
                </div>
                <div className="flex items-center justify-center gap-2">
                  {getTrendIcon()}
                  <span className={`text-xl font-bold ${getTrendColor()}`}>
                    {riskEvolution.trend === 'improving' ? 'Mejorando' : 
                     riskEvolution.trend === 'worsening' ? 'Empeorando' : 'Estable'}
                  </span>
                </div>
              </div>

              {/* First Score */}
              <div className="text-center">
                <div className="text-sm uppercase tracking-wider text-slate-500 mb-2">
                  Riesgo Inicial
                </div>
                <div className={`inline-flex px-4 py-2 rounded-xl text-2xl font-bold ${getRiskScoreColor(riskEvolution.first_score)}`}>
                  {riskEvolution.first_score}
                </div>
              </div>

              {/* Last Score */}
              <div className="text-center">
                <div className="text-sm uppercase tracking-wider text-slate-500 mb-2">
                  Riesgo Actual
                </div>
                <div className={`inline-flex px-4 py-2 rounded-xl text-2xl font-bold ${getRiskScoreColor(riskEvolution.last_score)}`}>
                  {riskEvolution.last_score}
                </div>
              </div>

              {/* Improvement */}
              <div className="text-center">
                <div className="text-sm uppercase tracking-wider text-slate-500 mb-2">
                  Mejora Total
                </div>
                <div className={`text-2xl font-bold ${riskEvolution.improvement_percentage > 0 ? 'text-green-400' : riskEvolution.improvement_percentage < 0 ? 'text-red-400' : 'text-slate-400'}`}>
                  {riskEvolution.improvement_percentage > 0 ? '+' : ''}{riskEvolution.improvement_percentage}%
                </div>
              </div>
            </div>

            {/* Visual Timeline */}
            <div className="mt-6 flex items-center gap-2">
              {riskEvolution.evolution?.map((point, idx) => (
                <React.Fragment key={idx}>
                  <div 
                    className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${getRiskScoreColor(point.risk_score)}`}
                    title={`v${point.version_number}: ${point.risk_score} puntos`}
                  >
                    {point.risk_score}
                  </div>
                  {idx < riskEvolution.evolution.length - 1 && (
                    <div className="flex-1 h-1 bg-slate-700 rounded relative">
                      <div 
                        className={`absolute inset-y-0 left-0 rounded ${point.risk_score > riskEvolution.evolution[idx + 1]?.risk_score ? 'bg-green-500' : 'bg-red-500'}`}
                        style={{ width: '100%' }}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </motion.div>
        )}

        {/* Compare Instructions */}
        {versions.length >= 2 && (
          <div className="mb-6 p-4 rounded-xl bg-purple-500/10 border border-purple-500/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <GitCompare className="w-5 h-5 text-purple-400" />
                <span className="text-purple-300">
                  Selecciona 2 versiones para comparar ({selectedVersions.length}/2 seleccionadas)
                </span>
              </div>
              {selectedVersions.length === 2 && (
                <button
                  onClick={fetchDiff}
                  disabled={loadingDiff}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-colors"
                >
                  {loadingDiff ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitCompare className="w-4 h-4" />}
                  Comparar
                </button>
              )}
            </div>
          </div>
        )}

        {/* Diff View */}
        <AnimatePresence>
          {diffData && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-8 p-6 rounded-2xl bg-slate-800/50 border border-slate-700"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <GitCompare className="w-5 h-5 text-purple-400" />
                  {diffData.comparing}
                </h3>
                <button
                  onClick={() => setDiffData(null)}
                  className="text-slate-400 hover:text-white text-sm"
                >
                  Cerrar
                </button>
              </div>

              {/* Diff Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-center">
                  <div className="flex items-center justify-center gap-1 text-green-400 mb-1">
                    <Plus className="w-4 h-4" />
                    <span className="text-xl font-bold">{diffData.diff?.stats?.additions || 0}</span>
                  </div>
                  <span className="text-xs text-green-300">Líneas añadidas</span>
                </div>
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-center">
                  <div className="flex items-center justify-center gap-1 text-red-400 mb-1">
                    <Minus className="w-4 h-4" />
                    <span className="text-xl font-bold">{diffData.diff?.stats?.deletions || 0}</span>
                  </div>
                  <span className="text-xs text-red-300">Líneas eliminadas</span>
                </div>
                <div className="p-3 rounded-lg bg-slate-500/10 border border-slate-500/30 text-center">
                  <div className="flex items-center justify-center gap-1 text-slate-400 mb-1">
                    <Equal className="w-4 h-4" />
                    <span className="text-xl font-bold">{diffData.diff?.stats?.similarity_percentage || 0}%</span>
                  </div>
                  <span className="text-xs text-slate-300">Similitud</span>
                </div>
                <div className={`p-3 rounded-lg text-center ${diffData.risk_comparison?.improved ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                  <div className={`flex items-center justify-center gap-1 mb-1 ${diffData.risk_comparison?.improved ? 'text-green-400' : 'text-red-400'}`}>
                    {diffData.risk_comparison?.improved ? <ArrowDown className="w-4 h-4" /> : <ArrowUp className="w-4 h-4" />}
                    <span className="text-xl font-bold">{Math.abs(diffData.risk_comparison?.change || 0)}</span>
                  </div>
                  <span className={`text-xs ${diffData.risk_comparison?.improved ? 'text-green-300' : 'text-red-300'}`}>
                    {diffData.risk_comparison?.improved ? 'Puntos menos riesgo' : 'Puntos más riesgo'}
                  </span>
                </div>
              </div>

              {/* Visual Diff */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {diffData.diff?.changes?.map((change, idx) => (
                  <div key={idx} className="font-mono text-sm">
                    {change.type === 'delete' && change.old_lines.map((line, i) => (
                      <div key={`del-${i}`} className="px-3 py-1 bg-red-500/20 border-l-4 border-red-500 text-red-300">
                        - {line}
                      </div>
                    ))}
                    {change.type === 'insert' && change.new_lines.map((line, i) => (
                      <div key={`ins-${i}`} className="px-3 py-1 bg-green-500/20 border-l-4 border-green-500 text-green-300">
                        + {line}
                      </div>
                    ))}
                    {change.type === 'replace' && (
                      <>
                        {change.old_lines.map((line, i) => (
                          <div key={`rep-old-${i}`} className="px-3 py-1 bg-red-500/20 border-l-4 border-red-500 text-red-300">
                            - {line}
                          </div>
                        ))}
                        {change.new_lines.map((line, i) => (
                          <div key={`rep-new-${i}`} className="px-3 py-1 bg-green-500/20 border-l-4 border-green-500 text-green-300">
                            + {line}
                          </div>
                        ))}
                      </>
                    )}
                    {change.type === 'equal' && change.old_lines.slice(0, 3).map((line, i) => (
                      <div key={`eq-${i}`} className="px-3 py-1 text-slate-500">
                        &nbsp; {line}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Version List */}
        <div className="space-y-3">
          {versions.map((version, idx) => {
            const isSelected = selectedVersions.includes(version.id);
            return (
              <motion.div
                key={version.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
                onClick={() => handleVersionSelect(version.id)}
                className={`p-4 rounded-xl border cursor-pointer transition-all ${
                  isSelected 
                    ? 'bg-purple-500/20 border-purple-500' 
                    : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold ${
                      isSelected ? 'bg-purple-500 text-white' : 'bg-slate-700 text-slate-300'
                    }`}>
                      v{version.version_number}
                    </div>
                    <div>
                      <div className="font-medium">
                        Versión {version.version_number}
                        {idx === 0 && <span className="ml-2 text-xs text-purple-400">(Actual)</span>}
                      </div>
                      <div className="text-sm text-slate-400 flex items-center gap-2">
                        <Clock className="w-3 h-3" />
                        {new Date(version.created_at).toLocaleDateString('es-CL', {
                          day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </div>
                      {version.notes && (
                        <div className="text-sm text-slate-500 mt-1">
                          {version.notes}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {version.risk_score !== null && (
                      <div className={`px-3 py-1 rounded-lg text-sm font-bold ${getRiskScoreColor(version.risk_score)}`}>
                        Riesgo: {version.risk_score}
                      </div>
                    )}
                    {version.risk_improvement > 0 && (
                      <div className="flex items-center gap-1 text-green-400 text-sm">
                        <ArrowDown className="w-4 h-4" />
                        {version.risk_improvement} pts
                      </div>
                    )}
                    <ChevronRight className={`w-5 h-5 transition-colors ${isSelected ? 'text-purple-400' : 'text-slate-600'}`} />
                  </div>
                </div>

                {/* Diff stats for this version */}
                {version.diff_stats && (
                  <div className="mt-3 pt-3 border-t border-slate-700/50 flex gap-4 text-xs">
                    <span className="text-green-400">+{version.diff_stats.additions} líneas</span>
                    <span className="text-red-400">-{version.diff_stats.deletions} líneas</span>
                    <span className="text-slate-500">{version.diff_stats.similarity_percentage}% similar</span>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Empty State */}
        {versions.length === 0 && (
          <div className="text-center py-12">
            <History className="w-16 h-16 mx-auto mb-4 text-slate-600" />
            <h3 className="text-xl font-semibold mb-2">Sin versiones guardadas</h3>
            <p className="text-slate-400 mb-6">
              Las versiones se crean automáticamente al analizar y mejorar tu contrato.
            </p>
            <Link
              to={`/analizar`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-xl transition-colors"
            >
              <FileText className="w-5 h-5" />
              Analizar Contrato
            </Link>
          </div>
        )}
      </main>
    </div>
  );
};

export default ContractVersionsPage;
